import { GoogleAds, useScreenDetect } from '@sgcmpteltd/uikit-next';
import type {
  OnSlotRenderEnded,
} from '@sgcmpteltd/uikit-next/components/google-ads';
import {
  GOOGLE_ADS_SLOT_TYPE_WITHIN_PAGE,
} from '@sgcmpteltd/uikit-next/components/google-ads';
import { useCssVariable } from '@sgcmpteltd/uikit-react';
import cx from 'clsx';
import { useEffect, useRef, useState } from 'react';

import styles from './styles.module.scss';
import type { Component } from './types';

export const ResponsiveMobileContent: Component = ({
  config,
  className,
  disableScale,
  onSlotRenderEnded,
}) => {
  const { isIpadMini } = useScreenDetect();
  const isIpadMiniMode = isIpadMini();
  const [isHidden, setIsHidden] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const containerElRef = useRef<HTMLDivElement | null>(null);
  const googleAdsContentElRef = useRef<HTMLDivElement | null>(null);
  const [adWidth, setAdWidth] = useState(() => {
    if (config.slot.type !== GOOGLE_ADS_SLOT_TYPE_WITHIN_PAGE) {
      return 0;
    }
    const { size } = config.slot;

    if (
      !Array.isArray(size)
      || !size.every(innerArray => Array.isArray(innerArray)
      && typeof innerArray[0] === 'number')
    ) {
      return 0;
    }

    if (Array.isArray(size) && typeof size[0] === 'number') {
      return size[0];
    }

    return size[0][0];
  });

  const {
    setCssVariable: setContainerElCssVariable,
  } = useCssVariable({
    targetElRef: containerElRef,
  });

  const {
    setCssVariable: setGoogleAdsContentElCssVariable,
  } = useCssVariable({
    targetElRef: googleAdsContentElRef,
  });

  // Pre-calculate and apply scale immediately
  useEffect(() => {
    if (
      config
      && config.slot.type === GOOGLE_ADS_SLOT_TYPE_WITHIN_PAGE
      && isIpadMiniMode
    ) {
      const { innerWidth } = window;
      const expectedAdWidth = adWidth || 0;

      if (Number(expectedAdWidth) > 0) {
        const scaleFactor = innerWidth / Number(expectedAdWidth);

        setGoogleAdsContentElCssVariable(
          styles.googleAdsContentContainerVarName,
          scaleFactor.toString(),
        );
      }
    }
  }, [adWidth, config, isIpadMiniMode, setGoogleAdsContentElCssVariable]);

  const onSlotRenderEndedRef = useRef<OnSlotRenderEnded>((e) => {
    const slotEl = document.getElementById(e.slot.getSlotElementId());

    if (slotEl) {
      const actualWidth = slotEl.clientWidth;

      setAdWidth(actualWidth);
      setContainerElCssVariable(
        styles.containerWidthVarName,
        actualWidth.toString(),
      );
      setContainerElCssVariable(
        styles.containerHeightVarName,
        slotEl.clientHeight.toString(),
      );

      // Recalculate scale with actual rendered width
      if (
        isIpadMiniMode
        && config.slot.type === GOOGLE_ADS_SLOT_TYPE_WITHIN_PAGE
      ) {
        const { innerWidth } = window;
        const scaleFactor = innerWidth / actualWidth;

        setGoogleAdsContentElCssVariable(
          styles.googleAdsContentContainerVarName,
          scaleFactor.toString(),
        );
      }
    }

    setIsLoading(false);

    if (!e.isEmpty) {
      setIsHidden(false);
      setIsLoaded(true);
    }

    if (onSlotRenderEnded) {
      onSlotRenderEnded(e);
    }
  });

  useEffect(() => {
    const { current: googleAdsContentEl } = googleAdsContentElRef;

    if (!googleAdsContentEl) {
      return undefined;
    }

    const onGoogleAdsContentSizeChange = () => {
      if (config) {
        const { slot: configSlot } = config;

        if (configSlot.type !== GOOGLE_ADS_SLOT_TYPE_WITHIN_PAGE) {
          return;
        }

        const { innerWidth } = window;

        setGoogleAdsContentElCssVariable(
          styles.googleAdsContentContainerVarName,
          isIpadMiniMode
            ? `${375 / Number(adWidth)}`
            : `${innerWidth / Number(adWidth)}`,
        );
      }
    };

    const googleAdsContentElResizeObserver = new ResizeObserver(() => {
      onGoogleAdsContentSizeChange();
    });

    googleAdsContentElResizeObserver.observe(googleAdsContentEl);

    onGoogleAdsContentSizeChange();

    return () => {
      googleAdsContentElResizeObserver.disconnect();
    };
  }, [adWidth, config, isIpadMiniMode, setGoogleAdsContentElCssVariable]);

  return (
    <div
      ref={containerElRef}
      className={cx(
        styles.containerRoot,
        isHidden && styles.hidden,
        className,
        disableScale && styles.disableScaleWrapper,
      )}>
      <div
        className={styles.containerGoogleAds}>
        {isLoading && (
          <div
            className={styles.containerLoadingPlaceholder}
            style={{
              width: isIpadMiniMode ? '100%' : `${adWidth}px`,
              height: adWidth ? `${(Number(adWidth) * 0.25)}px` : '100px',
            }} />
        )}
        <div
          ref={googleAdsContentElRef}
          className={cx(
            styles.containerGoogleAdsContent,
            isLoaded && styles.isLoaded,
          )}>
          <GoogleAds
            adUnitPath={config.adUnitPath}
            divId={config.divId}
            slot={config.slot}
            targetingMap={config.targetingMap}
            onSlotRenderEndedRef={onSlotRenderEndedRef} />
        </div>
      </div>
    </div>
  );
};
